import { useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useTriageAlert, type FlowAlert, type Verdict } from "@/lib/queries";

const SEVERITY_STYLES: Record<string, string> = {
  critical: "bg-red-500/10 text-red-600 border-red-500/20 dark:text-red-400",
  high:     "bg-orange-500/10 text-orange-600 border-orange-500/20 dark:text-orange-400",
  medium:   "bg-amber-500/10 text-amber-600 border-amber-500/20 dark:text-amber-400",
  low:      "bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400",
  info:     "bg-blue-500/10 text-blue-600 border-blue-500/20 dark:text-blue-400",
};

const VERDICT_OPTIONS: { value: Verdict; label: string; tone: string }[] = [
  { value: "benign",        label: "Benign",        tone: "border-emerald-500/40 hover:bg-emerald-500/10" },
  { value: "monitoring",    label: "Monitoring",    tone: "border-amber-500/40   hover:bg-amber-500/10" },
  { value: "investigating", label: "Investigating", tone: "border-blue-500/40    hover:bg-blue-500/10" },
  { value: "escalated",     label: "Escalated",     tone: "border-red-500/40     hover:bg-red-500/10" },
];

function fmt(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString([], {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

// asyncpg returns jsonb columns as strings (the codec isn't registered
// for JSON parsing on the way out). Be defensive — if it's a string, parse;
// if already an object, return as-is.
function parseJson(v: unknown): unknown {
  if (typeof v === "string") {
    try { return JSON.parse(v); } catch { return v; }
  }
  return v;
}

export function AlertDetailDrawer({
  alert,
  open,
  onOpenChange,
}: {
  alert: FlowAlert | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [notes, setNotes] = useState("");
  const triage = useTriageAlert();

  if (!alert) return null;

  const metrics = parseJson(alert.metrics) as Record<string, unknown>;
  const sample = parseJson(alert.sample) as Record<string, unknown> | null;
  const isTriaged = !!alert.triaged_at;

  function reset() {
    setVerdict(null);
    setNotes("");
    triage.reset();
  }

  async function submit() {
    if (!verdict || !alert) return;
    await triage.mutateAsync({
      id: alert.id,
      body: { verdict, notes: notes.trim() },
    });
    reset();
    onOpenChange(false);
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <SheetContent>
        <SheetHeader>
          <div className="flex items-center gap-3">
            <span
              className={cn(
                "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide",
                SEVERITY_STYLES[alert.severity],
              )}
            >
              {alert.severity}
            </span>
            <span className="font-mono text-xs text-muted-foreground">{alert.rule}</span>
            {isTriaged && (
              <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                triaged · {alert.triage_verdict}
              </span>
            )}
          </div>
          <SheetTitle className="mt-2">Alert #{alert.id}</SheetTitle>
          <SheetDescription>{alert.summary}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="grid grid-cols-[120px,1fr] gap-x-4 gap-y-3 text-sm">
            <div className="text-muted-foreground">Source</div>
            <div className="font-mono">{alert.src_addr}</div>

            <div className="text-muted-foreground">Detected at</div>
            <div className="tabular-nums">{fmt(alert.detected_at)}</div>

            <div className="text-muted-foreground">Window</div>
            <div className="tabular-nums text-xs text-muted-foreground">
              {fmt(alert.window_start)}<br />→ {fmt(alert.window_end)}
            </div>

            {isTriaged && (
              <>
                <div className="text-muted-foreground">Triaged at</div>
                <div className="tabular-nums">{fmt(alert.triaged_at)}</div>
                {alert.triage_notes && (
                  <>
                    <div className="text-muted-foreground">Notes</div>
                    <div className="text-foreground">{alert.triage_notes}</div>
                  </>
                )}
              </>
            )}
          </div>

          <div className="mt-6 space-y-4">
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Metrics
              </div>
              <pre className="overflow-x-auto rounded-md border border-border bg-muted/50 p-3 text-xs leading-relaxed text-foreground">
                {JSON.stringify(metrics, null, 2)}
              </pre>
            </div>

            {sample && (
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Sample
                </div>
                <pre className="overflow-x-auto rounded-md border border-border bg-muted/50 p-3 text-xs leading-relaxed text-foreground">
                  {JSON.stringify(sample, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>

        {!isTriaged && (
          <div className="border-t border-border bg-muted/20 px-6 py-5">
            <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Triage
            </div>

            <div className="grid grid-cols-2 gap-2">
              {VERDICT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setVerdict(opt.value)}
                  className={cn(
                    "rounded-md border px-3 py-2 text-sm font-medium transition-colors",
                    verdict === opt.value
                      ? "border-primary bg-primary text-primary-foreground"
                      : `border-border bg-background text-foreground ${opt.tone}`,
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="One-sentence note explaining the verdict (will surface in next audit)…"
              rows={3}
              className="mt-3 w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />

            {triage.isError && (
              <div className="mt-2 text-xs text-red-500">
                Failed to save triage. Try again.
              </div>
            )}

            <button
              type="button"
              disabled={!verdict || triage.isPending}
              onClick={submit}
              className={cn(
                "mt-3 w-full rounded-md px-4 py-2 text-sm font-medium transition-colors",
                verdict && !triage.isPending
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "cursor-not-allowed bg-muted text-muted-foreground",
              )}
            >
              {triage.isPending ? "Saving…" : verdict ? `Mark as ${verdict}` : "Pick a verdict"}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
